import React, { useState, useEffect } from "react";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [scrollY, setScrollY] = useState(0);
  const [visibleExperiences, setVisibleExperiences] = useState([]);

  useEffect(() => {
    setIsLoaded(true);
    
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    const observeExperiences = () => {
      const experienceElements = document.querySelectorAll('.experience-card');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleExperiences(prev => [...new Set([...prev, index])]);
          }
        });
      }, { threshold: 0.3 });

      experienceElements.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    };

    // Counter animation
    const animateCounters = () => {
      const counters = document.querySelectorAll('.counter');
      counters.forEach(counter => {
        const target = parseInt(counter.getAttribute('data-target'));
        let count = 0;
        const updateCounter = () => {
          if (count < target) {
            count++;
            counter.textContent = count;
            setTimeout(updateCounter, 50);
          }
        };
        setTimeout(updateCounter, 1000); // Start after 1 second delay
      });
    };

    window.addEventListener('scroll', handleScroll);
    const cleanup = observeExperiences();
    animateCounters();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cleanup();
    };
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ 
      behavior: 'smooth' 
    });
    setActiveSection(sectionId);
  };

  const experiences = [
    {
      title: "Founder & Software Developer — Frame Interventions / Frame Practice",
      period: "2025 – Present",
      description: "Designing and building EdTech tools for Arizona schools: an MTSS / pre-referral intervention tracker and an IEP implementation workspace for special education teams, related service providers, and administrators."
    },
    {
      title: "Owner / Managing Director — Simply SLPs, LLC",
      period: "August 2010 – Present (15+ years)",
      description: "Founded Simply SLPs, LLC to provide specialized special education professionals for school districts, charter schools, and private schools. Focus on leadership, team building, and quality staffing solutions."
    },
    {
      title: "Speech-Language Pathologist — Simply SLPs, LLC", 
      period: "August 2010 – Present (15+ years)",
      description: "Provides speech therapy services in various educational settings while managing business operations and staff supervision."
    },
    {
      title: "Speech Language Pathologist - My Therapy Company (MTC)",
      period: "January 2009 – August 2010", 
      description: "Provided school-based speech therapy services in Los Angeles, California."
    },
    {
      title: "Speech Language Pathologist - Pediatric Therapy Associates",
      period: "September 2003 – August 2008",
      description: "Provided comprehensive speech therapy services to pediatric clients over 5 years."
    },
    {
      title: "Speech Language Pathologist - NYC Board of Education",
      period: "January 1999 – July 2002",
      description: "Provided speech therapy services in New York City schools for over 3 years."
    }
  ];

  const specializations = [
    "Leadership & Team Building",
    "Supervisory Skills",
    "Educational Staffing Solutions",
    "K-12 Speech Therapy Services",
    "Special Education Programs",
    "Business Management",
    "Software Development",
    "EdTech Product Design"
  ];

  const projects = [
    {
      name: "Frame Interventions",
      tagline: "MTSS & pre-referral support for Arizona schools",
      description:
        "A workspace for school teams to organize the work that happens before special education referral — tracking interventions, fidelity, and student support team decisions in one clear place.",
      features: [
        "Track student interventions & progress",
        "Document supports tried with fidelity",
        "Assign providers and review dates",
        "Organize MTSS / SST team decisions",
        "Generate clearer pre-referral records"
      ],
      status: "Arizona Pilot",
      accent: "blue"
    },
    {
      name: "Frame Practice",
      tagline: "Implementation workspace for IEP teams",
      description:
        "Once a student has an IEP, the challenge shifts from writing the plan to implementing it well. Frame Practice gives sped teams, providers, and admins a shared workspace to deliver and monitor services.",
      features: [
        "Develop measurable IEP goals & data",
        "Schedule services across providers",
        "Monitor service minutes & fidelity",
        "Map student skills over time",
        "Admin visibility into implementation"
      ],
      status: "Arizona Pilot",
      accent: "amber"
    }
  ];

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-gray-900/95 backdrop-blur-sm z-50 transition-all duration-300">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="text-xl font-bold text-white" data-testid="nav-brand">Pete O'Rourke, M.S., CCC-SLP</div>
            <div className="hidden md:flex space-x-6">
              <button onClick={() => scrollToSection('hero')} className="text-gray-300 hover:text-blue-400 transition-colors">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-blue-400 transition-colors" data-testid="nav-about">About</button>
              <button onClick={() => scrollToSection('experience')} className="text-gray-300 hover:text-blue-400 transition-colors" data-testid="nav-experience">Experience</button>
              <button onClick={() => scrollToSection('projects')} className="text-gray-300 hover:text-blue-400 transition-colors" data-testid="nav-projects">Projects</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-blue-400 transition-colors" data-testid="nav-contact">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="particles">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>
        <div className="hero-overlay" style={{ opacity: Math.max(0.8, 1 - scrollY / 1000) }}></div>
        <div className="hero-content" style={{ transform: `translateY(${scrollY * 0.3}px)` }}>
          <div className={`transform transition-all duration-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="profile-image-container">
              <div className="profile-image floating">
                <img 
                  src="https://i.imgur.com/FPHkGm1.jpeg" 
                  alt="Pete O'Rourke, M.S., CCC-SLP" 
                  className="profile-photo"
                />
                <div className="profile-ring"></div>
                <div className="profile-ring-2"></div>
              </div>
            </div>
            <h1 className="hero-title typewriter">Pete O'Rourke</h1>
            <h2 className="hero-subtitle">M.S., CCC-SLP</h2>
            <p className="hero-description">Speech-Language Pathologist &nbsp;•&nbsp; Education Leader &amp; Software Developer</p>
            <p className="hero-tagline">Founder, Simply SLPs, LLC &nbsp;•&nbsp; Builder of Frame Interventions &amp; Frame Practice</p>

            {/* Arizona ESA Vendor Card */}
            <div className="esa-card" data-testid="esa-card">
              <div className="esa-badge">
                <svg className="esa-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 7.7l5.4-.8L12 2z" />
                </svg>
                <span>Registered Arizona ESA Vendor</span>
              </div>
              <h3 className="esa-title">Speech-Language Services Available Through Arizona ESA</h3>
              <p className="esa-body">
                Simply SLPs, LLC is a registered Arizona Empowerment Scholarship Account (ESA) vendor and is set up with <span className="esa-highlight">ClassWallet</span>. Families may use eligible ESA funds for private speech-language evaluations, individualized therapy, and consultation services provided by Pete O'Rourke, M.S., CCC-SLP.
              </p>
              <p className="esa-body esa-body-secondary">
                ClassWallet billing is available, helping families access services through their ESA funds more easily.
              </p>
              <a
                href="mailto:simplyslps@gmail.com?subject=Arizona%20ESA%20Speech-Language%20Services"
                className="esa-cta-button"
                data-testid="esa-cta-button"
              >
                Ask About ESA Services
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat-item pulse-animation">
                <span className="stat-number counter" data-target="25">0</span><span className="stat-number">+</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item pulse-animation">
                <span className="stat-number">Simply SLPs</span>
                <span className="stat-label">Founded 2010</span>
              </div>
            </div>
            <button 
              onClick={() => scrollToSection('contact')} 
              className="cta-button pulse-button"
            >
              <span>Get In Touch</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="section-title">Professional Summary</h2>
            <div className="section-divider"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                With over 25 years of experience as a Speech-Language Pathologist, Pete founded Simply SLPs, LLC in 2010 to connect school districts with specialized professionals and foster growth in educational programs.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                As Owner and Managing Director of Simply SLPs, LLC, Pete provides leadership and supervisory expertise, offering staffing solutions for various special education roles across Arizona.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                His focus on team building and quality professional development ensures educational programs receive the specialized support they need to succeed.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Pete is also a software developer building <span className="font-semibold text-gray-900">Frame Interventions</span> and <span className="font-semibold text-gray-900">Frame Practice</span> — tools designed to close the gap between supports planned, services delivered, and student progress over time.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Specializations</h3>
              <div className="grid grid-cols-2 gap-3">
                {specializations.map((spec, index) => (
                  <div key={index} className="specialization-tag">
                    {spec}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="section-title">Professional Experience</h2>
            <div className="section-divider"></div>
          </div>
          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div 
                key={index} 
                data-index={index}
                className={`experience-card ${visibleExperiences.includes(index) ? 'animate-in' : ''}`}
              >
                <div className="experience-timeline"></div>
                <div className="experience-content">
                  <h3 className="experience-title">{exp.title}</h3>
                  <p className="experience-period">{exp.period}</p>
                  <p className="experience-description">{exp.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section-padding bg-gray-50" data-testid="projects-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="projects-eyebrow">Now Building</span>
            <h2 className="section-title">Software for Arizona Schools</h2>
            <div className="section-divider"></div>
            <p className="projects-intro">
              Special education support does not begin with a referral, and it does not end when the IEP is finalized. These tools are built around the real work Arizona educators are doing every day.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`project-card project-accent-${project.accent}`}
                data-testid={`project-card-${index}`}
              >
                <div className="project-card-header">
                  <span className="project-status">{project.status}</span>
                  <h3 className="project-name">{project.name}</h3>
                  <p className="project-tagline">{project.tagline}</p>
                </div>
                <p className="project-description">{project.description}</p>
                <ul className="project-features">
                  {project.features.map((feature, i) => (
                    <li key={i} className="project-feature-item">
                      <svg className="project-check" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="projects-cta">
            <p>Inviting a small group of Arizona schools and districts to join our pilot.</p>
            <a href="mailto:simplyslps@gmail.com?subject=Frame%20Pilot%20Interest" className="projects-cta-button" data-testid="projects-cta-button">
              Request Pilot Access
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="section-title text-white">Contact Information</h2>
            <div className="section-divider bg-blue-400"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="contact-card">
              <div className="contact-icon">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                </svg>
              </div>
              <h3 className="contact-title">Email</h3>
              <a href="mailto:simplyslps@gmail.com" className="contact-link">
                simplyslps@gmail.com
              </a>
            </div>
            <div className="contact-card">
              <div className="contact-icon">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                </svg>
              </div>
              <h3 className="contact-title">Phone</h3>
              <a href="tel:+18053412334" className="contact-link">
                (805) 341-2334
              </a>
            </div>
            <div className="contact-card">
              <div className="contact-icon">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                </svg>
              </div>
              <h3 className="contact-title">Location</h3>
              <p className="contact-link">Chandler, Arizona</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-400 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p>&copy; 2026 Pete O'Rourke, M.S., CCC-SLP — Simply SLPs, LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;